import {
  Box,
  Button,
  ButtonBase,
  ButtonProps,
  IconButton,
  MenuItem,
  MenuList,
  Stack,
  Typography,
  TypographyProps,
} from "@mui/material";

import AppBadge from "@/common/components/Badge/AppBadge";
import AppButton from "@/common/components/Button/AppButton";
import AppDropdownButton from "@/common/components/Button/AppDropdownButton";
import AppDropdownIconButton from "@/common/components/Button/AppDropdownIconButton";
import AppIconButton, { AppIconButtonProps } from "@/common/components/Button/AppIconButton";
import AppSplitDropdownButton from "@/common/components/Button/AppSplitDropdownButton";
import InlineCode from "@/common/components/Code/InlineCode";
import AppIcon from "@/common/components/Icons/AppIcon";

export function DevButtons() {
  return (
    <Stack spacing={4}>
      <Typography component='div' variant='h1'>
        Buttons
      </Typography>

      <Stack spacing={2}>
        <Typography component='div' variant='h2'>
          <InlineCode>MUI ButtonBase</InlineCode>
        </Typography>

        <Stack direction='row' spacing={1}>
          <ButtonBase>Some text</ButtonBase>
        </Stack>
      </Stack>

      <Stack spacing={2}>
        <Typography component='div' variant='h2'>
          <InlineCode>MUI Button</InlineCode>
        </Typography>

        <Stack direction='column' spacing={1}>
          <Typography component='div' variant='h6'>
            Variants
          </Typography>
          <Stack direction='row' spacing={1}>
            <Button variant='text'>text</Button>
            <Button variant='outlined'>outlined</Button>
            <Button variant='contained'>contained</Button>
          </Stack>

          <Typography component='div' variant='h6'>
            Disabled
          </Typography>
          <Stack direction='row' spacing={1}>
            <Button variant='text' disabled>
              text
            </Button>
            <Button variant='outlined' disabled>
              outlined
            </Button>
            <Button variant='contained' disabled>
              contained
            </Button>
          </Stack>

          <Typography component='div' variant='h6'>
            Colors
          </Typography>
          <Stack direction='row' spacing={1}>
            <Button variant='text' color='inherit'>
              text inherit
            </Button>{" "}
            <Button variant='text' color='text'>
              text text
            </Button>
            <Button variant='text' color='primary'>
              text primary
            </Button>
            <Button variant='text' color='secondary'>
              text secondary
            </Button>
            <Button variant='text' color='info'>
              text info
            </Button>
            <Button variant='text' color='error'>
              text error
            </Button>
            <Button variant='text' color='warning'>
              text warning
            </Button>
            <Button variant='text' color='success'>
              text success
            </Button>
          </Stack>
          <Stack direction='row' spacing={1}>
            <Button variant='outlined' color='inherit'>
              outlined inherit
            </Button>
            <Button variant='outlined' color='text'>
              outlined text
            </Button>
            <Button variant='outlined' color='primary'>
              outlined primary
            </Button>
            <Button variant='outlined' color='secondary'>
              outlined secondary
            </Button>
            <Button variant='outlined' color='info'>
              outlined info
            </Button>
            <Button variant='outlined' color='error'>
              outlined error
            </Button>
            <Button variant='outlined' color='warning'>
              outlined warning
            </Button>
            <Button variant='outlined' color='success'>
              outlined success
            </Button>
          </Stack>
          <Stack direction='row' spacing={1}>
            <Button variant='contained' color='inherit'>
              contained inherit
            </Button>
            <Button variant='contained' color='text'>
              contained text
            </Button>
            <Button variant='contained' color='primary'>
              contained primary
            </Button>
            <Button variant='contained' color='secondary'>
              contained secondary
            </Button>
            <Button variant='contained' color='info'>
              contained info
            </Button>
            <Button variant='contained' color='error'>
              contained error
            </Button>
            <Button variant='contained' color='warning'>
              contained warning
            </Button>
            <Button variant='contained' color='success'>
              contained success
            </Button>
          </Stack>

          <Typography component='div' variant='h6'>
            Size
          </Typography>

          <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
            <Button variant='text' size='extraSmall'>
              extraSmall
            </Button>
            <Button variant='text' size='small'>
              small
            </Button>
            <Button variant='text' size='medium'>
              medium
            </Button>
            <Button variant='text' size='large'>
              large
            </Button>
          </Stack>

          <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
            <Button variant='outlined' size='extraSmall'>
              extraSmall
            </Button>
            <Button variant='outlined' size='small'>
              small
            </Button>
            <Button variant='outlined' size='medium'>
              medium
            </Button>
            <Button variant='outlined' size='large'>
              large
            </Button>
          </Stack>

          <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
            <Button variant='contained' size='extraSmall'>
              extraSmall
            </Button>
            <Button variant='contained' size='small'>
              small
            </Button>
            <Button variant='contained' size='medium'>
              medium
            </Button>
            <Button variant='contained' size='large'>
              large
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <Stack spacing={2}>
        <Typography component='div' variant='h2'>
          <InlineCode>MUI IconButton</InlineCode>
        </Typography>
        <Stack direction='column' spacing={1}>
          <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
            <IconButton size='extraSmall' title='extraSmall'>
              <AppIcon of='user' />
            </IconButton>
            <IconButton size='small' title='small'>
              <AppIcon of='user' />
            </IconButton>
            <IconButton size='medium' title='medium'>
              <AppIcon of='user' />
            </IconButton>
            <IconButton size='large' title='large'>
              <AppIcon of='user' />
            </IconButton>
          </Stack>

          <Stack direction='row' spacing={1}>
            <IconButton color='primary'>
              <AppIcon of='user' />
            </IconButton>
            <IconButton color='secondary'>
              <AppIcon of='user' />
            </IconButton>
            <IconButton color='info'>
              <AppIcon of='user' />
            </IconButton>
            <IconButton color='error'>
              <AppIcon of='user' />
            </IconButton>
            <IconButton color='warning'>
              <AppIcon of='user' />
            </IconButton>
            <IconButton color='success'>
              <AppIcon of='user' />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>

      <Stack spacing={2}>
        <Typography component='div' variant='h2'>
          <InlineCode>AppButton</InlineCode>
        </Typography>

        <Stack spacing={2}>
          <Typography component='div' variant='subtitle1'>
            Loading
          </Typography>

          <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
            <AppButton variant='contained' loading size='extraSmall'>
              extraSmall
            </AppButton>
            <AppButton variant='contained' loading size='small'>
              small
            </AppButton>
            <AppButton variant='contained' loading size='medium'>
              medium
            </AppButton>
            <AppButton variant='contained' loading size='large'>
              large
            </AppButton>
          </Stack>

          <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
            <AppButton variant='outlined' loading size='extraSmall'>
              extraSmall
            </AppButton>
            <AppButton variant='outlined' loading size='small'>
              small
            </AppButton>
            <AppButton variant='outlined' loading size='medium'>
              medium
            </AppButton>
            <AppButton variant='outlined' loading size='large'>
              large
            </AppButton>
          </Stack>

          <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
            <AppButton variant='text' loading size='extraSmall'>
              extraSmall
            </AppButton>
            <AppButton variant='text' loading size='small'>
              small
            </AppButton>
            <AppButton variant='text' loading size='medium'>
              medium
            </AppButton>
            <AppButton variant='text' loading size='large'>
              large
            </AppButton>
          </Stack>
        </Stack>
      </Stack>

      <Stack spacing={2}>
        <Typography component='div' variant='h2'>
          <InlineCode>AppIconButton</InlineCode>
        </Typography>

        <Stack spacing={2}>
          <Typography component='div' variant='h6'>
            Sizes, loading
          </Typography>
          <Stack gap={1}>
            <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
              <AppIconButton
                variant='default'
                size='extraSmall'
                tooltipProps={{ title: "extraSmall" }}
              >
                <AppIcon of='user' />
              </AppIconButton>
              <AppIconButton variant='default' size='small' tooltipProps={{ title: "small" }}>
                <AppIcon of='user' />
              </AppIconButton>
              <AppIconButton variant='default' size='medium' tooltipProps={{ title: "medium" }}>
                <AppIcon of='user' />
              </AppIconButton>
              <AppIconButton variant='default' size='large' tooltipProps={{ title: "large" }}>
                <AppIcon of='user' />
              </AppIconButton>
            </Stack>
            <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
              <AppIconButton
                variant='outlined'
                size='extraSmall'
                tooltipProps={{ title: "extraSmall" }}
              >
                <AppIcon of='user' />
              </AppIconButton>
              <AppIconButton variant='outlined' size='small' tooltipProps={{ title: "small" }}>
                <AppIcon of='user' />
              </AppIconButton>
              <AppIconButton variant='outlined' size='medium' tooltipProps={{ title: "medium" }}>
                <AppIcon of='user' />
              </AppIconButton>
              <AppIconButton variant='outlined' size='large' tooltipProps={{ title: "large" }}>
                <AppIcon of='user' />
              </AppIconButton>
            </Stack>
            <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
              <AppIconButton
                variant='contained'
                size='extraSmall'
                tooltipProps={{ title: "extraSmall" }}
              >
                <AppIcon of='user' />
              </AppIconButton>
              <AppIconButton variant='contained' size='small' tooltipProps={{ title: "small" }}>
                <AppIcon of='user' />
              </AppIconButton>
              <AppIconButton variant='contained' size='medium' tooltipProps={{ title: "medium" }}>
                <AppIcon of='user' />
              </AppIconButton>
              <AppIconButton variant='contained' size='large' tooltipProps={{ title: "large" }}>
                <AppIcon of='user' />
              </AppIconButton>
            </Stack>
          </Stack>

          <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
            <AppIconButton size='extraSmall' loading tooltipProps={{ title: "extraSmall" }}>
              <AppIcon of='user' />
            </AppIconButton>
            <AppIconButton size='small' loading tooltipProps={{ title: "small" }}>
              <AppIcon of='user' />
            </AppIconButton>
            <AppIconButton size='medium' loading tooltipProps={{ title: "medium" }}>
              <AppIcon of='user' />
            </AppIconButton>
            <AppIconButton size='large' loading tooltipProps={{ title: "large" }}>
              <AppIcon of='user' />
            </AppIconButton>
          </Stack>

          <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
            <AppIconButton
              size='large'
              variant='contained'
              color='default'
              tooltipProps={{ title: "large" }}
            >
              <AppIcon of='search' />
            </AppIconButton>
            <AppIconButton
              size='large'
              variant='contained'
              color='default'
              active
              tooltipProps={{ title: "large" }}
            >
              <AppIcon of='filter' />
            </AppIconButton>
            <AppIconButton
              size='large'
              variant='contained'
              color='default'
              tooltipProps={{ title: "large" }}
            >
              <AppIcon of='sort' />
            </AppIconButton>
            <AppIconButton
              size='large'
              variant='contained'
              color='default'
              tooltipProps={{ title: "large" }}
            >
              <AppIcon of='moreHoriz' />
            </AppIconButton>
          </Stack>
        </Stack>

        <Stack direction='column' spacing={1}>
          <Typography component='div' variant='h6'>
            Variants
          </Typography>

          <Stack direction='row' spacing={6}>
            {(
              [undefined, "default", "text", "outlined", "contained"] as Array<
                AppIconButtonProps["variant"]
              >
            ).map((variant, i) => {
              return (
                <Stack key={i} spacing={1}>
                  <Typography component='div' variant='subtitle1'>
                    variant={variant || "undefined"}
                  </Typography>
                  <Stack
                    spacing={1}
                    sx={{ alignItems: "flex-start", justifyContent: "flex-start" }}
                  >
                    {(
                      [
                        "inherit",
                        "default",
                        "text",
                        "primary",
                        "secondary",
                        "info",
                        "error",
                        "warning",
                        "success",
                      ] as Array<AppIconButtonProps["color"]>
                    ).map((color, j) => (
                      <Stack key={j} direction='row' spacing={1}>
                        <AppIconButton
                          size='medium'
                          shape='square'
                          variant={variant}
                          color={color}
                          tooltipProps={{ title: `color: ${color}` }}
                        >
                          <AppIcon of='user' />
                        </AppIconButton>

                        <AppIconButton
                          size='medium'
                          shape='square'
                          variant={variant}
                          color={color}
                          tooltipProps={{ title: `color: ${color}` }}
                        >
                          <AppBadge badgeContent={9} color='secondary' overlap='rectangular'>
                            <AppIcon of='user' />
                          </AppBadge>
                        </AppIconButton>

                        <AppIconButton shape='square' variant={variant} color={color} disabled>
                          <AppIcon of='user' />
                        </AppIconButton>

                        <AppIconButton shape='square' variant={variant} color={color} loading>
                          <AppIcon of='user' />
                        </AppIconButton>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Stack>

        <Stack spacing={2}>
          <Typography component='div' variant='h6'>
            Shapes
          </Typography>

          <Stack direction='row' spacing={6}>
            {([undefined, "round", "square"] as Array<AppIconButtonProps["shape"]>).map(
              (shape, i) => {
                return (
                  <Stack key={i} spacing={1}>
                    <Typography component='div' variant='subtitle1'>
                      shape={shape || "undefined"}
                    </Typography>
                    <Stack
                      spacing={1}
                      sx={{ alignItems: "flex-start", justifyContent: "flex-start" }}
                    >
                      {(
                        ["default", "text", "contained", "outlined"] as Array<
                          AppIconButtonProps["variant"]
                        >
                      ).map((variant, j) => (
                        <Stack key={j} direction='row' spacing={1}>
                          <AppIconButton
                            shape={shape}
                            variant={variant}
                            color='primary'
                            tooltipProps={{ title: `variant: ${variant}` }}
                          >
                            <AppIcon of='user' />
                          </AppIconButton>

                          <AppIconButton shape={shape} variant={variant} color='primary' disabled>
                            <AppIcon of='user' />
                          </AppIconButton>

                          <AppIconButton shape={shape} variant={variant} color='primary' loading>
                            <AppIcon of='user' />
                          </AppIconButton>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                );
              },
            )}
          </Stack>
        </Stack>

        <Stack spacing={2}>
          {(["h1", "subtitle1", "body1", "caption"] as Array<TypographyProps["variant"]>).map(
            (variant, i) => (
              <Typography key={i} variant={variant}>
                This the icon button in text{" "}
                <AppIconButton variant='text'>
                  <AppIcon of='user' />
                </AppIconButton>
                . Lorem ipsum{" "}
                <AppIconButton variant='text'>
                  <AppIcon of='vehicle' />
                </AppIconButton>{" "}
                dolor sit amet consectetur adipisicing elit.
              </Typography>
            ),
          )}
        </Stack>
      </Stack>

      <Typography component='div' variant='h2'>
        <InlineCode>AppDropdownButton</InlineCode>
      </Typography>
      <Box>
        <AppDropdownButton
          dropdownContent={
            <MenuList>
              <MenuItem>Status 1</MenuItem>
              <MenuItem>Status 2</MenuItem>
              <MenuItem>Status 3</MenuItem>
            </MenuList>
          }
        >
          Default
        </AppDropdownButton>

        <Typography component='div' variant='subtitle1'>
          button
        </Typography>
        <AppDropdownButton
          component='button'
          dropdownContent={
            <MenuList>
              <MenuItem>Status 1</MenuItem>
              <MenuItem>Status 2</MenuItem>
              <MenuItem>Status 3</MenuItem>
            </MenuList>
          }
        >
          Label
        </AppDropdownButton>

        <Typography component='div' variant='subtitle1'>
          iconButton
        </Typography>
        <AppDropdownButton
          component='iconButton'
          dropdownContent={
            <MenuList>
              <MenuItem>Status 1</MenuItem>
              <MenuItem>Status 2</MenuItem>
              <MenuItem>Status 3</MenuItem>
            </MenuList>
          }
        />

        <Typography component='div' variant='subtitle1'>
          expandIconVariant=outlined
        </Typography>
        <Box>
          <AppDropdownButton
            component='button'
            expandIconVariant='outlined'
            dropdownContent={
              <MenuList>
                <MenuItem>Status 1</MenuItem>
                <MenuItem>Status 2</MenuItem>
                <MenuItem>Status 3</MenuItem>
              </MenuList>
            }
          >
            Status
          </AppDropdownButton>

          <AppDropdownButton
            component='iconButton'
            expandIconVariant='outlined'
            dropdownContent={
              <MenuList>
                <MenuItem>Status 1</MenuItem>
                <MenuItem>Status 2</MenuItem>
                <MenuItem>Status 3</MenuItem>
              </MenuList>
            }
          />
        </Box>

        <Typography component='div' variant='subtitle1'>
          behaviors
        </Typography>
        <AppDropdownButton
          behavior='button'
          dropdownContent={
            <MenuList>
              <MenuItem>Status 1</MenuItem>
              <MenuItem>Status 2</MenuItem>
              <MenuItem>Status 3</MenuItem>
            </MenuList>
          }
        >
          behavior=button
        </AppDropdownButton>

        <AppDropdownButton
          behavior='dropdown'
          dropdownContent={
            <MenuList>
              <MenuItem>Status 1</MenuItem>
              <MenuItem>Status 2</MenuItem>
              <MenuItem>Status 3</MenuItem>
            </MenuList>
          }
        >
          behavior=dropdown
        </AppDropdownButton>

        <Typography component='div' variant='subtitle1'>
          loading
        </Typography>
        <AppDropdownButton
          component='button'
          isLoading
          dropdownContent={
            <MenuList>
              <MenuItem>Status 1</MenuItem>
              <MenuItem>Status 2</MenuItem>
              <MenuItem>Status 3</MenuItem>
            </MenuList>
          }
        >
          Loading
        </AppDropdownButton>

        <AppDropdownButton
          component='iconButton'
          isLoading
          dropdownContent={
            <MenuList>
              <MenuItem>Status 1</MenuItem>
              <MenuItem>Status 2</MenuItem>
              <MenuItem>Status 3</MenuItem>
            </MenuList>
          }
        >
          Loading
        </AppDropdownButton>

        <Typography component='div' variant='subtitle1'>
          Variants
        </Typography>
        <Stack direction='row' spacing={1}>
          {(["text", "outlined", "contained"] as Array<ButtonProps["variant"]>).map(
            (variant, i) => (
              <AppDropdownButton
                key={i}
                buttonProps={{ variant: variant }}
                dropdownContent={
                  <MenuList>
                    <MenuItem>Status 1</MenuItem>
                    <MenuItem>Status 2</MenuItem>
                    <MenuItem>Status 3</MenuItem>
                  </MenuList>
                }
              >
                Status
              </AppDropdownButton>
            ),
          )}
        </Stack>
      </Box>

      <Typography component='div' variant='h2'>
        <InlineCode>AppDropdownIconButton</InlineCode>
      </Typography>
      <Stack direction='row' spacing={1} alignItems='center'>
        <AppDropdownIconButton
          dropdownContent={
            <MenuList>
              <MenuItem>Status 1</MenuItem>
              <MenuItem>Status 2</MenuItem>
              <MenuItem>Status 3</MenuItem>
            </MenuList>
          }
        >
          <AppIcon of='money' />
        </AppDropdownIconButton>

        <AppDropdownIconButton
          dropdownContent={
            <MenuList>
              <MenuItem>Status 1</MenuItem>
              <MenuItem>Status 2</MenuItem>
              <MenuItem>Status 3</MenuItem>
            </MenuList>
          }
        >
          <AppIcon of='user' />
        </AppDropdownIconButton>

        <AppDropdownIconButton
          disabled
          dropdownContent={
            <MenuList>
              <MenuItem>Status 1</MenuItem>
              <MenuItem>Status 2</MenuItem>
              <MenuItem>Status 3</MenuItem>
            </MenuList>
          }
        >
          <AppIcon of='user' />
        </AppDropdownIconButton>

        <AppDropdownIconButton
          loading
          dropdownContent={
            <MenuList>
              <MenuItem>Status 1</MenuItem>
              <MenuItem>Status 2</MenuItem>
              <MenuItem>Status 3</MenuItem>
            </MenuList>
          }
        >
          <AppIcon of='user' />
        </AppDropdownIconButton>
      </Stack>

      <Stack spacing={2}>
        <Typography component='div' variant='h2'>
          <InlineCode>AppSplitDropdownButton</InlineCode>
        </Typography>

        <Box>
          <AppSplitDropdownButton
            options={[
              { content: "Option 1", buttonProps: { color: "primary" } },
              { content: "Option 2", buttonProps: { color: "success" } },
              { content: "Option 3", buttonProps: { color: "error" } },
            ]}
          />
        </Box>
      </Stack>
    </Stack>
  );
}
